import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import { SxProps } from '@mui/system';
import { useStacklineTheme } from '@stackline/ui';
import { GenericNavigationTabs } from 'src/components/BeaconV2Components/SubtabNav/SubtabNavigation';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';

import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';

import { useAppDispatch, useAppSelector, useLocation } from 'src/utils/Hooks/typedHooks';
import { getProductImageUrl } from 'src/utils/image';
import { ProductImage } from 'src/components/BeaconV2Components/ProductCard/ProductCard';
import colors from 'src/utils/colors';
import ContentGenSingleReview from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/ContentGenSingleReview';
import { getQueryParamValue } from 'src/utils/app';
import { entityOperations } from 'src/store/modules/entityService';
import { useContentGenReviewStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/stores';
import { Route, Switch } from 'react-router-dom';
import ProductContentGenProfile from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/ProductContentGenProfile';

export enum ContentGenTab {
  Generations = 'generations',
  Review = 'review',
  Profile = 'profile'
}

export enum ContentGenTabsDisplayName {
  Generations = 'Generations',
  Review = 'Review',
  Profile = 'Profile'
}

export enum ContentGenTabsPath {
  generations = '/content/generations',
  review = '/content/generate/review',
  profile = '/content/generate/profile'
}

// Get stackline SKU from query parameters
export const getStacklineSku = () => {
  return getQueryParamValue('sku', '');
};

// Check if it's single SKU generation (no contentGenId)
export const isSingleSkuGeneration = (pathname: string) => {
  return !getQueryParamValue('contentGenId', '') && !pathname.includes(ContentGenTabsPath.generations);
};

// Helper function to get current tab from pathname
const getCurrentTabFromPathname = (pathname: string): ContentGenTab => {
  if (pathname.includes(ContentGenTabsPath.review)) {
    return ContentGenTab.Review;
  }
  if (pathname.includes(ContentGenTabsPath.profile)) {
    return ContentGenTab.Profile;
  }
  return ContentGenTab.Generations;
};

export const ContentGenNavigationTabs = () => {
  const { beaconNavigator } = useBeaconNavigator();
  const location = useLocation();
  const stacklineSku = getStacklineSku();
  const { hasContentGenItems } = useContentGenReviewStore();
  const isInSingleSkuMode = isSingleSkuGeneration(location.pathname);

  const tab = getCurrentTabFromPathname(location.pathname);

  const generationsTab = {
    label: ContentGenTabsDisplayName.Generations,
    onClick: () => {
      beaconNavigator.setPathname(`${ContentGenTabsPath.generations}`);
      beaconNavigator.updateSearchParams({ sku: stacklineSku });
      beaconNavigator.go();
    },
    value: ContentGenTab.Generations
  };

  const profileTab = {
    label: ContentGenTabsDisplayName.Profile,
    onClick: () => {
      beaconNavigator.setPathname(`${ContentGenTabsPath.profile}`);
      beaconNavigator.updateSearchParams({ sku: stacklineSku });
      beaconNavigator.go();
    },
    value: ContentGenTab.Profile
  };

  const reviewTab = {
    label: ContentGenTabsDisplayName.Review,
    onClick: () => {},
    value: ContentGenTab.Review,
    disabled: !hasContentGenItems()
  };

  const tabs = isInSingleSkuMode ? [profileTab, reviewTab] : [generationsTab, reviewTab];

  return <GenericNavigationTabs tab={tab} tabs={tabs} />;
};

export const RetailerSkuCreditBalanceHeader = () => {
  const stacklineSku = getStacklineSku();
  const retailerSku = useAppSelector((state) => state.entityService?.mainEntity?.retailerSku);
  const location = useLocation();
  const { contentGenItemsToReview, getCurrentContentGenItemIndex } = useContentGenReviewStore();

  const isReviewPage = location.pathname.includes(ContentGenTabsPath.review);

  if (!isReviewPage) {
    return null;
  }

  return (
    <Flex flexDirection="row" gap={12}>
      <StyledBox sx={{ width: '157px' }}>
        <ProductImage
          imageUri={getProductImageUrl(stacklineSku)}
          mainDisplayTitle={stacklineSku}
          secondaryDisplayTitle={stacklineSku}
          width={40}
          height={40}
          imageStyle={{
            flexShrink: 0,
            background: 'lightgray',
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat'
          }}
        />
        <Flex flexDirection="column" gap={4}>
          <Text
            variant="body2"
            sx={{
              color: colors.grey,
              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: 500,
              textTransform: 'uppercase'
            }}
          >
            RETAILER SKU
          </Text>
          <Text
            variant="body2"
            sx={{
              color: colors.darkBlue,
              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: 400,
              textTransform: 'uppercase'
            }}
          >
            {retailerSku}
          </Text>
        </Flex>
      </StyledBox>
      {contentGenItemsToReview.length > 1 && (
        <StyledBox>
          <Flex flexDirection="column" gap={4}>
            <Text
              variant="body2"
              sx={{
                color: colors.grey,
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 500,
                textTransform: 'uppercase'
              }}
            >
              CONTENT TO REVIEW
            </Text>
            <Text
              variant="body1"
              sx={{
                color: colors.darkBlue,
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 400,
                textTransform: 'uppercase'
              }}
            >{`${getCurrentContentGenItemIndex() + 1} of ${contentGenItemsToReview.length}`}</Text>
          </Flex>
        </StyledBox>
      )}
      {false && (
        <StyledBox>
          <Text
            variant="body2"
            sx={{
              color: colors.grey,
              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: 500,
              textTransform: 'uppercase'
            }}
          >
            CREDIT BALANCE
          </Text>
        </StyledBox>
      )}
    </Flex>
  );
};

export const SubtabHeader = ({ Header, Cards }: { Header: React.ReactNode; Cards?: React.ReactNode }) => {
  return (
    <Flex flexDirection="row" justifyContent="space-between">
      <Box>{Header}</Box>
      {Cards}
    </Flex>
  );
};

export const Header = () => {
  return (
    <>
      <ContentTopNav />
      <ContentGenNavigationTabs />
    </>
  );
};

const Cards = () => {
  return <RetailerSkuCreditBalanceHeader />;
};

SubtabHeader.Header = Header;
SubtabHeader.Cards = Cards;

export const ContentTopNav = () => {
  return (
    <Flex justifyContent="space-between" flexDirection="row" alignItems="center">
      <Text
        variant="h2"
        sx={{
          lineHeight: '40px',
          marginRight: 'auto'
        }}
      >
        Generate Content
      </Text>
    </Flex>
  );
};

export const StyledBox = ({ children, sx }: { children: React.ReactNode; sx?: SxProps }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        padding: '10px 12px',
        alignItems: 'center',
        gap: '12px',
        width: '172px',
        height: '60px',
        borderRadius: '8px',
        border: `1px solid ${colors.graysSecondary}`,
        background: colors.contentGenBackground,
        ...sx
      }}
    >
      {children}
    </Box>
  );
};

const ContentGenPageLayout = () => {
  const theme = useStacklineTheme();
  const stacklineSku = getStacklineSku();
  const dispatch = useAppDispatch();
  const retailerId = getQueryParamValue('rid', '');

  useEffect(() => {
    if (stacklineSku && retailerId) {
      dispatch(entityOperations.fetchMainEntity('product', stacklineSku, retailerId));
    }
  }, [dispatch, stacklineSku, retailerId]);

  return (
    <Box>
      <Box marginBottom={theme.spacing.lg}>
        <SubtabHeader Header={<Header />} Cards={<Cards />} />
        <BasePageContainer>
          <Switch>
            <Route path={ContentGenTabsPath.review} component={ContentGenSingleReview} />
            <Route path={ContentGenTabsPath.profile} component={ProductContentGenProfile} />
          </Switch>
        </BasePageContainer>
      </Box>
    </Box>
  );
};

export default ContentGenPageLayout;
